import { useCallback } from 'react';
import type {
  EnvironmentKey,
  ConfigEntityGetEndpointResponse,
} from '@web-config-app/core';
import { ConfigEntityAuthAction } from '@web-config-app/core';
import { buildConfigEntityAuthHeaders } from '@web-config-app/api';
import { useQueryClient } from '@tanstack/react-query';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import { getEndpointParamsWithValues } from '../../utilities/get-endpoint-params-with-values/get-endpoint-params-with-values';
import { getIdParam } from '../../utilities/get-id-param/get-id-param';
import { getIncludeParam } from '../../utilities/get-include-param/get-include-param';

export interface UseConfigEntityGetByIdQueryProps {
  /**
   * The environment to fetch the config entity from
   */
  sourceEnvironment: EnvironmentKey;
  /**
   * Whether to include all referenced entities in the response
   */
  includeReferences?: boolean;
  /**
   * The default domain entity type used for auth headers (e.g. 'config-campaign').
   * Can be overridden per-call via the second argument of the returned function.
   * If neither is provided, the request will fail with 403 due to missing auth headers, so we throw an error here to prevent the request from failing silently.
   */
  domainEntityType?: string;
}

/**
 * Hook that provides an imperative function to fetch a single config entity by ID.
 *
 * This hook returns a **callback function** (not state) that you call when you need to
 * fetch a specific entity on-demand. Unlike declarative hooks, this does not automatically
 * fetch data or manage loading/error state.
 *
 * The returned function accepts an entity ID and returns an object with the entity data
 * and any included referenced entities. It uses React Query's `queryClient.fetchQuery`
 * under the hood for caching.
 *
 * @param props.sourceEnvironment - The environment to fetch the entity from
 * @param props.includeReferences - Whether to include all referenced entities. Defaults to `false`
 * @returns An async function that accepts a config entity ID and an optional domainEntityType
 * override for auth headers, and returns a Promise resolving to an object with `data` and
 * `included` properties. Throws on error.
 *
 * @example
 * // Fetch a single entity with error handling
 * ```typescript
 * const getEntityById = useConfigEntityGetByIdQuery({
 *   sourceEnvironment: 'production',
 *   domainEntityType: 'config-campaign',
 *   includeReferences: true
 * });
 *
 * const handleFetch = async () => {
 *   try {
 *     const result = await getEntityById('entity-123', 'config-campaign');
 *     console.log('Entity:', result.data);
 *     console.log('References:', result.included);
 *   } catch (error) {
 *     console.error('Failed to fetch:', error);
 *   }
 * };
 * ```
 */

export const useConfigEntityGetByIdQuery = ({
  sourceEnvironment,
  includeReferences = false,
  domainEntityType,
}: UseConfigEntityGetByIdQueryProps) => {
  const queryClient = useQueryClient();
  const { configEntity } = useConfigApp();
  const sourceEnvironmentFetch = useEnvironmentFetch(sourceEnvironment);

  const headerEntityType = domainEntityType;

  const { endpointFetch: getEntityFromSource } =
    getEntityEndpoint<ConfigEntityGetEndpointResponse>(
      configEntity.endpoints.get,
      sourceEnvironmentFetch,
    );

  return useCallback(
    async (configEntityId: string, perCallDomainEntityType?: string) => {
      const params = getEndpointParamsWithValues([
        getIdParam(
          configEntity.endpoints.get.pathParameters ?? [],
          configEntity.endpoints.get.path,
          configEntityId,
        ),
        getIncludeParam(
          configEntity.endpoints.get.queryParameters ?? [],
          undefined,
          includeReferences,
          includeReferences ? 'allReferences' : undefined,
        ),
      ]);

      const resolvedDomainEntityType =
        perCallDomainEntityType ?? headerEntityType;

      if (!resolvedDomainEntityType) {
        throw new Error(
          'domainEntityType is required either as a hook prop or per-call argument',
        );
      }

      const response = await queryClient.fetchQuery({
        queryKey: [
          sourceEnvironment,
          configEntity.endpoints.get.path,
          configEntityId,
        ],
        queryFn: () =>
          getEntityFromSource({
            params,
            headers: buildConfigEntityAuthHeaders(
              resolvedDomainEntityType,
              ConfigEntityAuthAction.Get,
            ),
          }),
        staleTime: 0,
        retry: false,
      });

      return response;
    },
    [
      sourceEnvironment,
      configEntity,
      getEntityFromSource,
      queryClient,
      includeReferences,
      headerEntityType,
    ],
  );
};
